import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import { BODEGAS, IBodegasPagingRes } from '@/shared/interfaces/erp/inventario';
import { useUiStore } from '@/store/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchBodegas = (params?: GetBodegasParams, enabled = true) => {
  return useQuery({
    queryKey: ['bodegas', ...Object.values(params || {})],

    queryFn: async () => {
      return getBodegas(params);
    },
    enabled: enabled,
    refetchOnWindowFocus: false,
  });
};

export const useGetBodega = (id: string) => {
  return useQuery({
    queryKey: ['bodegas', id],
    queryFn: async () => {
      return getBodega(id);
    },
  });
};

export const useCreateBodegas = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createBodegas,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bodegas'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Bodega creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateBodegas = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateBodegas,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bodegas'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Bodegas actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetBodegasParams = {
  page?: number;
  page_size?: number;
  codigo?: string;
  nombre?: string;
  descripcion?: string;
  responsable?: string;
  empresa?: string;
  ciudad?: string;
  ubicacion?: string;
  estado?: boolean;

  es_externa?: boolean;

  // Bypass del filtro por asignación: solo lo honra el backend si el usuario tiene
  // el módulo USUARIOS.USUARIOS (picker de administración de usuarios). En pantallas
  // operativas NO se envía, por lo que el listado queda restringido a las bodegas asignadas.
  todas_bodegas?: boolean;

  // filter by state
  filterByState?: boolean;
};

export type CreateBodegasParams = Omit<BODEGAS, 'id_bodega'>;
export type UpdateBodegasParams = {
  id: string;
  data: CreateBodegasParams;
};

export const getBodegas = async (params?: GetBodegasParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<IBodegasPagingRes>(
    `${urlBackend}api/v1/bodega/?${queryParams}`,
    true
  );
};

export const getBodega = async (id: string) => {
  try {
    return await get<BODEGAS>(`${urlBackend}api/v1/bodega/${id}/`, true);
  } catch (error) {
    return handleAxiosError(error);
  }
};
export const createBodegas = async (data: CreateBodegasParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<BODEGAS>(`${urlBackend}api/v1/bodega/`, data, true);
};

export const updateBodegas = async ({ id, data }: UpdateBodegasParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<BODEGAS>(`${urlBackend}api/v1/bodega/${id}/`, data, true);
};


///* fetch para el autcomplete

export const fetchAutocompleteBodega = async (
  /* searchValue: string,
  page: number, */
  params?: string
): Promise<{ options: BODEGAS[]; hasMore: boolean }> => {


  const response = await get<IBodegasPagingRes>(`${urlBackend}api/v1/bodega/?${params}`, true);

  return {
    options: response.data.data,
    hasMore: true,
  };
};


///* fetch autocomplete

export const fetchBodegaAutocomplete = async (query: string, page: number, params: any) => {
  try {
    const res = await getBodegas({
      ...params,
      serie: query,
      page,
      page_size: 20,
    });
    const items = res.data.data;
    const total = res.data?.count ?? 0;

    const options: string[] = items
      .map((item) => {
        return item.nombre
      })
      .filter((s: unknown): s is string => typeof s === "string")

    const hasMore = (page * 20 < total);

    return { options, hasMore };
  } catch (err) {
    // En caso de error, no cortes el scroll del usuario
    console.error("fetchPageSeries error:", err);
    return { options: [] as string[], hasMore: false };
  }
};